import type { UserDto } from '@tms/shared';
import { Pencil, Plus, Trash2, UserX } from 'lucide-react';
import { useState } from 'react';
import { useDebounced } from '../../lib/use-debounced';
import { toast } from 'sonner';
import { PageHeader } from '../../components/layout/PageHeader';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Input,
  Menu,
  MenuItem,
  Pagination,
  SkeletonRows,
  Table,
  TableWrap,
  Td,
  Th,
} from '../../components/ui';
import { useEnumLabels, useErrorText, useFormat, useT } from '../../i18n';
import { usePermissions } from '../auth/permissions';
import { UserFormDialog } from './UserFormDialog';
import { useDeleteUser, useUpdateUser, useUsers } from './api';

export const UsersPage = () => {
  const { can, user: currentUser } = usePermissions();
  const { t } = useT();
  const { date } = useFormat();
  const { role: roleLabel } = useEnumLabels();
  const { errorText } = useErrorText();
  const manage = can('user:manage');

  const [page, setPage] = useState(1);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [grantLogin, setGrantLogin] = useState('');
  const [editing, setEditing] = useState<UserDto | null>(null);
  const [pendingDelete, setPendingDelete] = useState<UserDto | null>(null);

  const debouncedSearch = useDebounced(search.trim());
  const { data, isPending, error, refetch } = useUsers({
    page,
    includeInactive,
    ...(debouncedSearch ? { q: debouncedSearch } : {}),
  });

  /** Opens the grant dialog, prefilled with a GitLab login when there is one. */
  const grantAccess = (login = '') => {
    setGrantLogin(login);
    setCreateOpen(true);
  };
  const update = useUpdateUser();
  const remove = useDeleteUser();

  const setActive = async (user: UserDto, isActive: boolean) => {
    try {
      await update.mutateAsync({ userId: user.id, input: { isActive } });
      toast.success(
        isActive
          ? t('users.activated', { login: user.login })
          : t('users.deactivated2', { login: user.login }),
      );
    } catch (updateError) {
      toast.error(errorText(updateError, 'users.updateFailed'));
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await remove.mutateAsync(pendingDelete.id);
      toast.success(t('users.deleted', { login: pendingDelete.login }));
      setPendingDelete(null);
    } catch (deleteError) {
      toast.error(errorText(deleteError, 'users.deleteFailed'));
    }
  };

  return (
    <>
      <PageHeader
        title={t('users.title')}
        description={t('users.subtitle')}
        actions={
          manage ? (
            <Button variant="primary" onClick={() => grantAccess()}>
              <Plus className="size-4" />
              {t('users.new')}
            </Button>
          ) : null
        }
      />

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-2.5">
          <Input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder={t('users.searchPlaceholder')}
            className="w-full sm:w-64"
            aria-label={t('users.searchLabel')}
          />
          <label className="flex items-center gap-2 text-[13px] text-ink-muted">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(event) => {
                setIncludeInactive(event.target.checked);
                setPage(1);
              }}
              className="size-4 rounded border-border"
            />
            {t('users.showInactive')}
          </label>
        </div>

        {error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : isPending ? (
          <SkeletonRows />
        ) : data.data.length === 0 ? (
          <EmptyState
            title={t('users.empty')}
            {...(debouncedSearch && manage
              ? {
                  description: t('users.emptySearchHint'),
                  action: (
                    <Button variant="primary" onClick={() => grantAccess(debouncedSearch)}>
                      <Plus className="size-4" />
                      {t('users.grantTo', { login: debouncedSearch })}
                    </Button>
                  ),
                }
              : {})}
          />
        ) : (
          <>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>{t('common.name')}</Th>
                    <Th>{t('users.columnLogin')}</Th>
                    <Th>{t('users.columnRole')}</Th>
                    <Th>{t('common.status')}</Th>
                    <Th>{t('common.created')}</Th>
                    <Th className="w-10" />
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((user) => (
                    <tr key={user.id}>
                      <Td>
                        <span className="font-medium">{user.name}</span>
                        {user.id === currentUser?.id ? (
                          <Badge className="ml-2">{t('users.you')}</Badge>
                        ) : null}
                      </Td>
                      <Td className="font-mono text-[12px] text-ink-muted">{user.login}</Td>
                      <Td>{roleLabel(user.role)}</Td>
                      <Td>
                        <span
                          className={
                            user.isActive
                              ? 'text-[12px] font-medium text-pass'
                              : 'text-[12px] font-medium text-ink-subtle'
                          }
                        >
                          {user.isActive ? t('users.active') : t('users.deactivated')}
                        </span>
                      </Td>
                      <Td className="text-ink-muted">{date(user.createdAt)}</Td>
                      <Td>
                        {manage ? (
                          <Menu>
                            <MenuItem onSelect={() => setEditing(user)}>
                              <Pencil className="size-3.5" />
                              {t('users.editUser')}
                            </MenuItem>
                            <MenuItem
                              disabled={user.id === currentUser?.id}
                              onSelect={() => void setActive(user, !user.isActive)}
                            >
                              <UserX className="size-3.5" />
                              {user.isActive ? t('users.deactivate') : t('users.reactivate')}
                            </MenuItem>
                            <MenuItem
                              destructive
                              disabled={user.id === currentUser?.id}
                              onSelect={() => setPendingDelete(user)}
                            >
                              <Trash2 className="size-3.5" />
                              {t('users.deleteUser')}
                            </MenuItem>
                          </Menu>
                        ) : null}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
            <Pagination meta={data.meta} onPageChange={setPage} />
          </>
        )}
      </Card>

      <UserFormDialog open={createOpen} onOpenChange={setCreateOpen} initialLogin={grantLogin} />

      {editing ? (
        <UserFormDialog open onOpenChange={(open) => !open && setEditing(null)} user={editing} />
      ) : null}

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={t('users.deleteTitle', { login: pendingDelete?.login ?? '' })}
        description={t('users.deleteHint')}
        confirmLabel={t('users.deleteUser')}
        loading={remove.isPending}
        onConfirm={() => void confirmDelete()}
      />
    </>
  );
};
